import React from 'react';

import {
  StudioWithLocationsFragment,
  useClassListFiltersQuery,
} from '../../../graphql/types';
import { useClassesQueryVariables } from '../../../hooks/useQueryParams';
import useTracking from '../../../hooks/useTracking';
import {
  EventCategories,
  EventNames,
  EventSubCategories,
} from '../../../utils/trackingEvents';
import { AppliedFilters } from '../../reusable/AppliedFilters';
import { Divider } from '../../reusable/Divider';

interface AppliedFiltersProps {}
export const ClassesAppliedFilters: React.FC<AppliedFiltersProps> = () => {
  const [queryParams, setQueryParams] = useClassesQueryVariables();
  const { data } = useClassListFiltersQuery();
  const { track } = useTracking();

  const removeFilter = (filterOption: { label: string; value: string }) => {
    track(EventNames.RemoveTag, {
      event_category: EventCategories.Classes,
      event_subcategory: EventSubCategories.Filters,
      event_label: filterOption.label,
    });

    const isLocationId = (queryParams.locationIds || []).includes(
      filterOption.value,
    );
    const isStudioId = (queryParams.studioIds || []).includes(
      filterOption.value,
    );

    setQueryParams({
      ...queryParams,
      locationIds: isLocationId
        ? (queryParams.locationIds || []).filter(
            (id) => id !== filterOption.value,
          )
        : queryParams.locationIds,
      studioIds: isStudioId
        ? (queryParams.studioIds || []).filter(
            (id) => id !== filterOption.value,
          )
        : queryParams.studioIds,
    });
  };

  const clearAllFilters = () => {
    track(EventNames.ClearAll, {
      event_category: EventCategories.Classes,
      event_subcategory: EventSubCategories.Filters,
    });
    setQueryParams({
      ...queryParams,
      locationIds: undefined,
      studioIds: undefined,
    });
  };

  const locationIds = queryParams.locationIds || [];
  const studioIds = queryParams.studioIds || [];

  const hasFilters = locationIds.length > 0 || studioIds.length > 0;

  if (!hasFilters) {
    return null;
  }

  // Get studios from coachData
  const studios =
    data?.self?.coachData?.clients?.edges
      .map((edge) => edge.node?.studios)
      .flat() || [];

  // Create hierarchical studio and location options for proper label lookup
  const studioLocationOptions: Array<{ label: string; value: string }> = [];
  studios.forEach((studio) => {
    if (studio) {
      // Add studio
      studioLocationOptions.push({
        label: studio.name || '',
        value: studio.id || '',
      });

      // Add locations for this studio
      studio.locations?.edges?.forEach(({ node }) => {
        if (node) {
          studioLocationOptions.push({
            label: node.name || '',
            value: node.id || '',
          });
        }
      });
    }
  });

  const appliedFilters: string[] = [];

  function getStudioLocationIds(studio: StudioWithLocationsFragment): string[] {
    return (
      studio.locations?.edges?.map(({ node }) => node?.id).filter(Boolean) || []
    );
  }

  // Add studios as applied filters if:
  // studio is selected and (has no locations OR all locations are selected)
  studioIds.forEach((studioId: string) => {
    const studio = studios.find((s) => s?.id === studioId);
    if (studio) {
      const locationIdsForStudio = getStudioLocationIds(studio);
      const allLocationsSelected =
        locationIdsForStudio.length > 0 &&
        locationIdsForStudio.every((locId) => locationIds.includes(locId));
      if (locationIdsForStudio.length === 0 || allLocationsSelected) {
        appliedFilters.push(studioId);
      }
    }
  });

  // Add locationIds as applied filters only if their parent studio is not already in appliedFilters
  studios.forEach((studio) => {
    if (studio) {
      // If studio is not in appliedFilters, add its selected locations
      if (!appliedFilters.includes(studio.id)) {
        const locationIdsForStudio = getStudioLocationIds(studio);
        locationIdsForStudio.forEach((locationId: string) => {
          if (locationIds.includes(locationId)) {
            appliedFilters.push(locationId);
          }
        });
      }
    }
  });

  return (
    <>
      <AppliedFilters
        allFilters={[...studioLocationOptions]}
        appliedFilters={appliedFilters}
        clearAllFilters={clearAllFilters}
        name="classes"
        removeFilter={removeFilter}
      />
      <Divider $marginBottom={24} $marginTop={16} />
    </>
  );
};
